import { useEffect } from 'react';

// ============================================================
// Seo — per-route document metadata manager
// ============================================================
// This site is a CRA SPA (no SSR). The static <title>/<meta>/<link rel=canonical>
// in public/index.html are correct ONLY for the homepage. Without per-route
// overrides, EVERY route (/contact, /privacy, /blog, ...) serves the homepage
// canonical (https://qcoda.com/), so Google reports them as
// "Alternative page with proper canonical tag" and refuses to index them.
//
// This component imperatively updates the existing head tags on mount so that:
//   - <link rel="canonical"> points at the *current* URL (self-referential)
//   - <title>, description, og:*, twitter:* reflect the current page
//
// It UPDATES the single existing canonical tag rather than appending a new one,
// so there is never more than one canonical on the page. Mutations run in
// useEffect, which executes inside both real browsers and the react-snap /
// Googlebot headless renderers — so the corrected tags are captured.

const SITE_URL = 'https://qcoda.com';
const DEFAULT_IMAGE = `${SITE_URL}/qcoda_og.png`;

function upsertMeta(selectorAttr, key, content) {
    if (content == null) return;
    let el = document.head.querySelector(`meta[${selectorAttr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(selectorAttr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function upsertCanonical(href) {
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
    if (!data) return;
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

export default function Seo({
    title,
    description,
    path = '/',
    image,
    type = 'website',
    noindex = false,
    jsonLd = null,
    jsonLdId = 'route-jsonld',
    author = null,
    publishedTime = null,
}) {
    useEffect(() => {
        const url = `${SITE_URL}${path}`;
        const img = image ? (image.startsWith('http') ? image : `${SITE_URL}${image}`) : DEFAULT_IMAGE;

        if (title) document.title = title;
        upsertCanonical(url);

        if (description) upsertMeta('name', 'description', description);
        upsertMeta('name', 'robots', noindex
            ? 'noindex, follow'
            : 'index, follow, max-image-preview:large, max-snippet:-1');
        if (author) upsertMeta('name', 'author', author);

        upsertMeta('property', 'og:type', type);
        upsertMeta('property', 'og:title', title);
        upsertMeta('property', 'og:description', description);
        upsertMeta('property', 'og:url', url);
        upsertMeta('property', 'og:image', img);

        // Article-specific Open Graph signals (used by social + some answer engines).
        if (type === 'article') {
            if (publishedTime) upsertMeta('property', 'article:published_time', publishedTime);
            if (author) upsertMeta('property', 'article:author', author);
        }

        upsertMeta('name', 'twitter:title', title);
        upsertMeta('name', 'twitter:description', description);
        upsertMeta('name', 'twitter:image', img);

        upsertJsonLd(jsonLdId, jsonLd);

        // Clean up the route-specific JSON-LD on unmount so a stale block does
        // not leak onto the next page (the homepage's static JSON-LD lives in
        // index.html and is never touched by this component).
        return () => {
            const el = document.getElementById(jsonLdId);
            if (el) el.remove();
        };
    }, [title, description, path, image, type, noindex, jsonLd, jsonLdId, author, publishedTime]);

    return null;
}
